import { FunctionComponent, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import DashboardLayout from '../../../components/layout/layout'
import { useGetResourcesByItem } from '../../../hooks/api/user'
import { handleFileClick } from '../../../utils/fileHandler'

const S5ProductTraining: FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Fetch S5 Product Training data from category 65
  const { data: resources, isLoading } = useGetResourcesByItem('65');

  const handleVideoClick = (video: any) => {
    setSelectedVideo(video);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVideo(null);
  };

  const VideoPlayer = ({ video }: { video: any }) => (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <video
        controls
        style={{
          width: '100%',
          height: 'auto',
          maxHeight: '70vh',
          backgroundColor: '#000',
        }}
        preload="metadata"
      >
        <source src={video.downloadUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Box>
  );

  return (
    <DashboardLayout 
      title="Sales Tools" 
      initialBreadcrumbs="Sales Tools" 
      levelBreadcrumbs="" 
      breadcrumbs="Sales Tools" 
      activeMenu="resources"
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Sales Tools: S5 Product Training
        </Typography>

        {isLoading ? (
          <>
            <Skeleton width="38%" />
            <Skeleton width="100%" />
            <Skeleton width="100%" />
            <Skeleton width="61%" />
          </>
        ) : (
          resources && Array.isArray(resources) && resources.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {resources.map((video, index) => (
                <Box
                  key={video.itemId || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px 0',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      borderRadius: 1,
                    },
                  }}
                                     onClick={() => {
                     if (video.filename && video.filename.toLowerCase().endsWith('.mp4')) {
                       // For video files, open in dialog
                       handleVideoClick(video);
                     } else if (video.downloadUrl) {
                       // For other files, use the smart file handler
                       handleFileClick(video.downloadUrl, video.filename || '');
                     }
                   }}
                 >
                   <Typography
                    sx={{
                      textDecoration: 'none',
                      color: '#1976d2',
                      fontWeight: 400,
                      ...theme.typography.body1,
                      textAlign: 'left',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#1565c0',
                      },
                    }}
                  >
                    {video.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'gray' }}>
              No training videos available for this category.
            </Typography>
          )
        )}

        {/* Video Player Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              backgroundColor: '#000',
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#000', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6">
              {selectedVideo?.title}
            </Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, backgroundColor: '#000' }}>
            {selectedVideo && <VideoPlayer video={selectedVideo} />}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#000', p: 2 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ color: 'white' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Back to Sales Tools button - at the bottom like main Sales Tools page */}
        <Box sx={{ textAlign: 'left', mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/resources')}
            sx={{
              bgcolor: '#c8102e',
              textTransform: 'none',
              fontWeight: 600,
              ...theme.typography.button,
              '&:hover': { bgcolor: '#a30f24' },
            }}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default S5ProductTraining; 